import { useEffect, useMemo, useState } from "react";
import {
  CommentContent,
  GlobalSearchCategory,
  GlobalSearchFormattedData,
  GlobalSearchSelectOption,
  IssueSheetContent,
  MeetingContent,
  ProductStrategyContent,
  RatingChangeContent,
} from "data/@types/GlobalSearchData";
import {
  globalSearchList,
  globalSearchQuery,
} from "data/services/api/globalSearch";
import { FirmContent } from "data/@types/Firm";
import { SortingOrder } from "utils/sort";
import { useGlobalSearchStore } from "data/store/global-search/GlobalSearch";
import { useAdvancedViewFiltersStore } from "data/store/global-search/AdvancedViewFiltersStore";

const categoryToDocumentTypeMap: Record<GlobalSearchSelectOption, string> = {
  All: "",
  Firms: "FIRM",
  "Products/Strategies": "PRODUCT_STRATEGY",
  Comments: "COMMENT",
  Meetings: "MEETING",
  "Rating Changes": "RATING_CHANGE",
  "Issue Sheet Updates": "ISSUE_SHEET",
};

type SearchApiResponse = {
  issue_sheet: {
    total: number;
    content: IssueSheetContent[];
    hasOnlyPartialMatches: boolean;
  };
  firm: {
    total: number;
    content: FirmContent[];
    hasOnlyPartialMatches: boolean;
  };
  rating_change: {
    total: number;
    content: RatingChangeContent[];
    hasOnlyPartialMatches: boolean;
  };
  comment: {
    total: number;
    content: CommentContent[];
    hasOnlyPartialMatches: boolean;
  };
  meeting: {
    total: number;
    content: MeetingContent[];
    hasOnlyPartialMatches: boolean;
  };
  product_strategy: {
    total: number;
    content: ProductStrategyContent[];
    hasOnlyPartialMatches: boolean;
  };
};

type UseSearchReturn = {
  data: GlobalSearchFormattedData[];
  isLoading: boolean;
  selectedItem: GlobalSearchSelectOption;
  updateSelectedItem: (item: GlobalSearchSelectOption) => void;
  fetchGlobalSearchList: (option: GlobalSearchSelectOption) => void;
};

const formatApiResponse = (
  data: SearchApiResponse,
): GlobalSearchFormattedData[] => {
  const categories: {
    key: keyof SearchApiResponse;
    name: GlobalSearchCategory;
  }[] = [
    { key: "firm", name: "Firms" },
    { key: "product_strategy", name: "Products/Strategies" },
    { key: "comment", name: "Comments" },
    { key: "meeting", name: "Meetings" },
    { key: "rating_change", name: "Rating Changes" },
    { key: "issue_sheet", name: "Issue Sheet Updates" },
  ];

  return categories.map(({ key, name }) => {
    const value = data[key];
    return {
      name,
      total: value?.total ?? 0,
      content: value?.content ?? [],
      hasOnlyPartialMatches: value?.hasOnlyPartialMatches ?? false,
    };
  });
};

const EMPTY_DATA: GlobalSearchFormattedData[] = [
  { name: "Firms", total: 0, content: [], hasOnlyPartialMatches: false },
  {
    name: "Products/Strategies",
    total: 0,
    content: [],
    hasOnlyPartialMatches: false,
  },
  { name: "Comments", total: 0, content: [], hasOnlyPartialMatches: false },
  { name: "Meetings", total: 0, content: [], hasOnlyPartialMatches: false },
  {
    name: "Rating Changes",
    total: 0,
    content: [],
    hasOnlyPartialMatches: false,
  },
  {
    name: "Issue Sheet Updates",
    total: 0,
    content: [],
    hasOnlyPartialMatches: false,
  },
];

export const useGlobalSearch = (
  searchTerm: string,
  sortByCreatedDate?: SortingOrder,
): UseSearchReturn => {
  const [data, setData, selectedItem, updateSelectedItem] =
    useGlobalSearchStore((state) => [
      state.globalSearchData,
      state.updateGlobalSearchData,
      state.selectedItem,
      state.updateSelectedItem,
    ]);
  const meetingFilterCriteria = useAdvancedViewFiltersStore(
    (state) => state.meetingFilterCriteria,
  );
  const commentFilterCriteria = useAdvancedViewFiltersStore(
    (state) => state.commentFilterCriteria,
  );
  const [isLoading, setIsLoading] = useState(false);

  const activeFilterCriteria = meetingFilterCriteria ?? commentFilterCriteria;

  const filteredDocumentTypes = useMemo(() => {
    if (meetingFilterCriteria) return ["MEETING"];
    if (commentFilterCriteria) return ["COMMENT"];
    return undefined;
  }, [meetingFilterCriteria, commentFilterCriteria]);

  useEffect(() => {
    if (!searchTerm.length) {
      setData(EMPTY_DATA);
      return;
    }

    setIsLoading(true);

    const fetchQuery = async () => {
      try {
        const { data } = await globalSearchQuery(
          searchTerm,
          sortByCreatedDate,
          activeFilterCriteria,
          filteredDocumentTypes,
        );
        setData(formatApiResponse(data));
      } catch (err) {
        setData(EMPTY_DATA);
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    const debounce = setTimeout(fetchQuery, 400);
    return () => clearTimeout(debounce);
  }, [
    searchTerm,
    sortByCreatedDate,
    activeFilterCriteria,
    filteredDocumentTypes,
  ]);

  const fetchGlobalSearchList = async (option: GlobalSearchSelectOption) => {
    const documentType = categoryToDocumentTypeMap[option];

    setIsLoading(true);
    try {
      const { data } = await globalSearchList(documentType);
      setData(formatApiResponse(data));
    } catch (err) {
      setData(EMPTY_DATA);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    data,
    isLoading,
    selectedItem,
    updateSelectedItem,
    fetchGlobalSearchList,
  };
};
